import { assertJpegQuality } from '@/core/export/validate'
import { ExportFormat, type ExportOptions } from '@/core/export/types'
import type { Size } from '@/core/geometry'

export const ESTIMATED_BYTES_PER_PIXEL: Readonly<Record<ExportFormat, number>> = {
  [ExportFormat.Jpeg]: 0.45,
  [ExportFormat.Png]: 2.2,
}

export function validateEstimatedBytesPerPixel(
  values: Readonly<Record<ExportFormat, number>> = ESTIMATED_BYTES_PER_PIXEL,
): void {
  for (const format of Object.values(ExportFormat)) {
    const value = values[format]
    if (!Number.isFinite(value) || value <= 0) {
      throw new Error(`Estimated bytes per pixel for "${format}" must be a finite positive number`)
    }
  }
}

export function estimateExportBytes(targetSize: Size, options: ExportOptions): number {
  if (!Number.isFinite(targetSize.width) || !Number.isFinite(targetSize.height)
    || targetSize.width <= 0 || targetSize.height <= 0) {
    throw new Error('Export size estimate requires finite positive dimensions')
  }
  assertJpegQuality(options.jpegQuality)
  const pixels = targetSize.width * targetSize.height
  switch (options.format) {
    case ExportFormat.Jpeg:
      return pixels * ESTIMATED_BYTES_PER_PIXEL[ExportFormat.Jpeg] * (options.jpegQuality / 0.95)
    case ExportFormat.Png:
      return pixels * ESTIMATED_BYTES_PER_PIXEL[ExportFormat.Png]
    default:
      throw new Error(`Cannot estimate unknown export format "${String(options.format)}"`)
  }
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    throw new Error('Byte formatting requires a finite non-negative value')
  }
  const megabytes = bytes / (1024 * 1024)
  return megabytes >= 1
    ? `~${megabytes.toFixed(1)} MB`
    : `~${Math.round(bytes / 1024)} KB`
}
